/**
 * Dev Mode Settings Component
 *
 * Provides a dropdown UI for switching API targets at runtime
 * when using dev Cognito authentication.
 *
 * Only visible when dev mode is available (VITE_ENVIRONMENT=dev, not deployed).
 */

import React, { useState, useRef, useEffect } from 'react';
import type { ApiTarget } from '../config/types';
import { useDevModeConfig } from '../hooks/useDevModeConfig';
import { Button, useTheme, spacing } from '@fti/design-system';

interface DevModeSettingsProps {
  /** Optional compact mode (icon only) */
  compact?: boolean;
}

/** API target options with display labels */
const API_TARGET_OPTIONS: { value: ApiTarget; label: string; description: string }[] = [
  {
    value: 'local',
    label: 'Local API',
    description: 'http://localhost:8000'
  },
  {
    value: 'dev',
    label: 'Dev API',
    description: 'Development server'
  }
];

/**
 * Dev Mode Settings dropdown
 *
 * Renders a gear icon that opens a dropdown for API target selection.
 * Hidden when dev mode is not available.
 */
export function DevModeSettings({ compact = false }: DevModeSettingsProps): JSX.Element | null {
  const { config, isDevModeAvailable, setApiTarget, getEffectiveApiUrl } = useDevModeConfig();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  // Theme-aware colors from design system
  const bgColor = theme.colors.background.paper;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Don't render if dev mode is not available
  if (!isDevModeAvailable) {
    return null;
  }

  const handleSelectTarget = (target: ApiTarget) => {
    setApiTarget(target);
    setIsOpen(false);
  };

  const currentOption = API_TARGET_OPTIONS.find(opt => opt.value === config.apiTarget);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Toggle button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        title="API Target Settings"
        style={{
          background: theme.colors.semantic.warning.light,
          border: `1px solid ${theme.colors.semantic.warning.default}`,
          color: theme.colors.neutral[900],
          padding: compact ? spacing.xs : `${spacing.xs} ${spacing.sm}`,
        }}
      >
        {/* Gear icon */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
        {!compact && <span style={{ marginLeft: spacing.xs }}>{currentOption?.label || 'API'}</span>}
      </Button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.35rem)',
            right: 0,
            minWidth: '200px',
            background: bgColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '0.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: `${spacing.sm} ${spacing.sm}`,
              borderBottom: `1px solid ${borderColor}`,
              fontSize: '0.75rem',
              color: mutedColor,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            API Target
          </div>

          {/* Options */}
          {API_TARGET_OPTIONS.map((option) => {
            const isSelected = config.apiTarget === option.value;
            return (
              <button
                key={option.value}
                onClick={() => handleSelectTarget(option.value)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                  padding: `${spacing.sm} ${spacing.sm}`,
                  background: isSelected ? theme.colors.semantic.warning.light : 'transparent',
                  border: 'none',
                  borderLeft: isSelected ? `3px solid ${theme.colors.semantic.warning.default}` : '3px solid transparent',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? theme.colors.neutral[900] : textColor
                  }}
                >
                  {option.label}
                  {isSelected && ' ✓'}
                </span>
                <span
                  style={{
                    fontSize: '0.7rem',
                    color: mutedColor,
                    marginTop: '0.15rem'
                  }}
                >
                  {option.description}
                </span>
              </button>
            );
          })}

          {/* Current URL */}
          <div
            style={{
              padding: `${spacing.xs} ${spacing.sm}`,
              borderTop: `1px solid ${borderColor}`,
              fontSize: '0.7rem',
              color: mutedColor,
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}
          >
            → {getEffectiveApiUrl()}
          </div>
        </div>
      )}
    </div>
  );
}
